import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Button, CardTitle, Alert, Spinner } from 'reactstrap';
import TableContainer from '../../../components/Common/DataTableContainer';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { RepositoryFactory } from '../../../repositories';
import { ProcesoProveedorRepository } from '../../../repositories/ProcesoProveedorRepository';
import type { ProcesoProveedor } from '../../../types/collections/ProcesoProveedor';
import ProcesoProveedorModal from './ProcesoProveedorModal';
import AvatarModal from '../../../components/Modals/AvatarModal';
// Default avatar - using require for compatibility
const defaultAvatar = require('../../../assets/images/users/avatar-6.jpg');

// Simple email regex for client-side validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ProcesoProveedorPage: React.FC = () => {
  // Function to get avatar URL
  const getAvatarUrl = (proveedor: ProcesoProveedor) => {
    if (proveedor.logo) {
      return `${process.env.REACT_APP_PB_URL}/api/files/proceso_proveedor/${proveedor.id}/${proveedor.logo}`;
    }
    return defaultAvatar;
  };

  // Function to open avatar modal
  const openAvatarModal = (proveedor: ProcesoProveedor) => {
    setAvatarModal({
      isOpen: true,
      avatarUrl: getAvatarUrl(proveedor),
      proveedorName: proveedor.nombre
    });
  };

  // Function to close avatar modal
  const closeAvatarModal = () => {
    setAvatarModal({ isOpen: false, avatarUrl: '', proveedorName: '' });
  };

  // Cast to concrete repository to access custom methods
  const repo = useMemo(
    () => RepositoryFactory.getRepository<ProcesoProveedor>('proceso_proveedor') as ProcesoProveedorRepository,
    []
  );

  const [items, setItems] = useState<ProcesoProveedor[]>([]);
  const [inactiveItems, setInactiveItems] = useState<ProcesoProveedor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [isFetching, setIsFetching] = useState(false);

  // Para confirmación de restaurar y desactivar
  const [showRestoreAlert, setShowRestoreAlert] = useState(false);
  const [proveedorToRestore, setProveedorToRestore] = useState<ProcesoProveedor | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Para confirmación de desactivar
  const [showDeactivateAlert, setShowDeactivateAlert] = useState(false);
  const [proveedorToDeactivate, setProveedorToDeactivate] = useState<ProcesoProveedor | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState<ProcesoProveedor | null>(null);
  
  // Avatar modal state
  const [avatarModal, setAvatarModal] = useState({ isOpen: false, avatarUrl: '', proveedorName: '' });

  const fetchData = useCallback(async () => {
    if (isFetching) return; // Evitar llamadas duplicadas

    try {
      setIsFetching(true);
      setLoading(true);

      // Obtener todos los proveedores de una vez (como en clientes)
      const allProveedores = await repo.getListWithFilters(1, 1000, {
        search
      });

      // Separar proveedores activos e inactivos
      const activos = allProveedores.items.filter(proveedor => proveedor.activo !== false);
      const inactivos = allProveedores.items.filter(proveedor => proveedor.activo === false);

      // Aplicar paginación solo a los activos
      const startIndex = pageIndex * pageSize;
      const endIndex = startIndex + pageSize;
      const activosPaginados = activos.slice(startIndex, endIndex);

      setItems(activosPaginados);
      setTotal(activos.length);
      setInactiveItems(inactivos);
    } catch (e) {
      setError('Error al cargar proveedores');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [pageIndex, pageSize, search, repo, isFetching]);

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize, search, repo]);

  const onGlobalSearch = useCallback((value: string) => {
    setSearch(value);
    setPageIndex(0);
  }, []);

  // Mostrar alerta de confirmación para restaurar
  const handleRestore = (proveedor: ProcesoProveedor) => {
    setProveedorToRestore(proveedor);
    setShowRestoreAlert(true);
    // Hacer scroll hacia arriba para mostrar el alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const confirmRestore = async () => {
    if (!proveedorToRestore) return;
    setRestoring(true);
    try {
      await repo.activate(proveedorToRestore.id);
      setShowRestoreAlert(false);
      setProveedorToRestore(null);
      fetchData();
    } catch (e) {
      setError('No se pudo actualizar el estado');
    } finally {
      setRestoring(false);
    }
  };

  const cancelRestore = () => {
    setShowRestoreAlert(false);
    setProveedorToRestore(null);
  };

  // Mostrar alerta de confirmación para desactivar
  const handleDeactivate = (proveedor: ProcesoProveedor) => {
    setProveedorToDeactivate(proveedor);
    setShowDeactivateAlert(true);
    // Hacer scroll hacia arriba para mostrar el alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const confirmDeactivate = async () => {
    if (!proveedorToDeactivate) return;
    setDeactivating(true);
    try {
      await repo.deactivate(proveedorToDeactivate.id);
      setShowDeactivateAlert(false);
      setProveedorToDeactivate(null);
      fetchData();
    } catch (e) {
      setError('No se pudo actualizar el estado');
    } finally {
      setDeactivating(false);
    }
  };

  const cancelDeactivate = () => {
    setShowDeactivateAlert(false);
    setProveedorToDeactivate(null);
  };

  const handleCreate = () => {
    setEditingProveedor(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (proveedor: ProcesoProveedor) => {
    setEditingProveedor(proveedor);
    setIsEditModalOpen(true);
  };

  const handleSave = async (data: Partial<ProcesoProveedor>) => {
    try {
      if (editingProveedor) {
        await repo.update(editingProveedor.id, data);
      } else {
        await repo.create(data);
      }
      fetchData();
    } catch (e) {
      throw new Error('Error al guardar el proveedor');
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingProveedor(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProveedor(null);
  };

  // Function to reload data (like in clientes page)
  const reloadProveedores = () => {
    fetchData();
  };

  // Columnas para proveedores activos
  const columns = useMemo(
    () => [
      {
        header: 'Nombre',
        accessorKey: 'nombre',
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: 'Avatar',
        accessorKey: 'logo',
        colSpan: 1,
        cell: (info: any) => {
          const proveedor = info.row.original;
          return (
            <div className="col-md-12" style={{ maxWidth: 135, display: "flex", justifyContent: "center" }}>
              <Button
                color="primary"
                size="sm"
                onClick={() => openAvatarModal(proveedor)}
                className="btn-icon"
                title="Ver logo"
              >
                <i className="fas fa-image"></i>
              </Button>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: { style: { width: "12.85%", minWidth: "140px" } },
      },
      {
        header: 'Correo',
        accessorKey: 'correo',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'NIT',
        accessorKey: 'nit',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Teléfono',
        accessorKey: 'telefono',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Dirección',
        accessorKey: 'direccion',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Acciones',
        accessorKey: 'acciones',
        cell: ({ row }: any) => {
          const p: ProcesoProveedor = row.original;
          return (
            <div style={{ display: 'flex', gap: 4, minWidth: 140, maxWidth: 180 }}>
              <Button color="warning" size="sm" className="me-2" onClick={() => handleEdit(p)}>
                <i className="fas fa-pen me-1"></i> Editar
              </Button>
              <Button color="danger" size="sm" onClick={() => handleDeactivate(p)}>
                <i className="fas fa-eraser me-1"></i> Desactivar
              </Button>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: { style: { width: "1%", whiteSpace: "nowrap" } },
      },
    ],
    []
  );

  // Columnas para proveedores inactivos
  const columnsInactivos = useMemo(
    () => [
      {
        header: 'Nombre',
        accessorKey: 'nombre',
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: 'Avatar',
        accessorKey: 'logo',
        colSpan: 1,
        cell: (info: any) => {
          const proveedor = info.row.original;
          return (
            <div className="col-md-12" style={{ maxWidth: 135, display: "flex", justifyContent: "center" }}>
              <Button
                color="primary"
                size="sm"
                onClick={() => openAvatarModal(proveedor)}
                className="btn-icon"
                title="Ver logo"
              >
                <i className="fas fa-image"></i>
              </Button>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: { style: { width: "12.85%", minWidth: "140px" } },
      },
      {
        header: 'Correo',
        accessorKey: 'correo',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'NIT',
        accessorKey: 'nit',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Teléfono',
        accessorKey: 'telefono',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Dirección',
        accessorKey: 'direccion',
        cell: (info: any) => info.getValue() ?? '',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Acciones',
        accessorKey: 'acciones',
        cell: ({ row }: any) => {
          const p: ProcesoProveedor = row.original;
          return (
            <div style={{ display: 'flex', gap: 4, minWidth: 140, maxWidth: 180 }}>
              <Button color="success" size="sm" onClick={() => handleRestore(p)}>
                <i className="fas fa-undo me-1"></i> Activar
              </Button>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: { style: { width: "1%", whiteSpace: "nowrap" } },
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Administración" breadcrumbItem="Proceso Proveedor" />
        
        {/* Alertas de confirmación */}
      {showRestoreAlert && proveedorToRestore && (
        <Alert
          color="info"
          isOpen={showRestoreAlert}
          toggle={cancelRestore}
          fade={true}
          transition={{ timeout: 150 }}
          className="d-flex align-items-center justify-content-between"
        >
          <div>
            <strong>¿Está seguro?</strong> Esto restaurará el proveedor <b>{proveedorToRestore.nombre}</b>.
          </div>
          <div>
            <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
              {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
            </Button>
            <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
              Cancelar
            </Button>
          </div>
        </Alert>
      )}

      {showDeactivateAlert && proveedorToDeactivate && (
        <Alert
          color="warning"
          isOpen={showDeactivateAlert}
          toggle={cancelDeactivate}
          fade={true}
          transition={{ timeout: 150 }}
          className="d-flex align-items-center justify-content-between"
        >
          <div>
            <strong>¿Está seguro?</strong> Esto desactivará el proveedor <b>{proveedorToDeactivate.nombre}</b>.
          </div>
          <div>
            <Button color="danger" size="sm" className="me-2" onClick={confirmDeactivate} disabled={deactivating}>
              {deactivating ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
            </Button>
            <Button color="secondary" size="sm" onClick={cancelDeactivate} disabled={deactivating}>
              Cancelar
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="mb-0">Proveedores Activos</h5>
            <div>
              <Button color="primary" size="sm" onClick={handleCreate}>
                <i className="fas fa-plus me-1"></i> Agregar
              </Button>
              <Button
                color="secondary"
                size="sm"
                className="ms-2"
                title="Recargar tabla"
                onClick={reloadProveedores}
              >
                <i className="fas fa-sync-alt"></i>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert color="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center">
              <Spinner color="primary" />
              <p className="mt-2">Cargando proveedores...</p>
            </div>
          ) : (
            <div className="table-rep-plugin" style={{ overflow: "visible" }}>
              <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                <TableContainer
                  columns={columns}
                  data={items}
                  isGlobalFilter={false}
                  isPagination={true}
                  isCustomPageSize={true}
                  isBordered={true}
                  tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                  theadClass=""
                  divClassName="table-responsive"
                  paginationWrapper='dataTables_paginate paging_simple_numbers'
                  SearchPlaceholder="Buscar..."
                  pagination="pagination"
                  buttonClass="btn btn-primary"
                  buttonName="Agregar"
                  isAddButton={false}
                  handleUserClick={() => {}}
                  isJobListGlobalFilter={false}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalRecords={total}
                />
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Tabla de Proveedores Inactivos */}
      {inactiveItems.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <h5 className="mb-0">Proveedores Inactivos</h5>
          </CardHeader>
          <CardBody>
            <div className="table-rep-plugin" style={{ overflow: "visible" }}>
              <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                <TableContainer
                  columns={columnsInactivos}
                  data={inactiveItems}
                  isGlobalFilter={false}
                  isPagination={false}
                  isCustomPageSize={false}
                  isBordered={true}
                  tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                  theadClass=""
                  divClassName="table-responsive"
                  paginationWrapper='dataTables_paginate paging_simple_numbers'
                  SearchPlaceholder="Buscar..."
                  pagination="pagination"
                  buttonClass="btn btn-primary"
                  buttonName="Agregar"
                  isAddButton={false}
                  handleUserClick={() => {}}
                  isJobListGlobalFilter={false}
                  pageIndex={0}
                  setPageIndex={() => {}}
                  pageSize={10}
                  setPageSize={() => {}}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modales */}
      <ProcesoProveedorModal
        isOpen={isCreateModalOpen}
        toggle={handleCloseCreateModal}
        onSave={handleSave}
        proveedor={null}
        title="Crear Proveedor"
      />

      <ProcesoProveedorModal
        isOpen={isEditModalOpen}
        toggle={handleCloseEditModal}
        onSave={handleSave}
        proveedor={editingProveedor}
        title="Editar Proveedor"
      />

      <AvatarModal
        isOpen={avatarModal.isOpen}
        toggle={closeAvatarModal}
        avatarUrl={avatarModal.avatarUrl}
        clienteName={avatarModal.proveedorName}
      />
      </div>
    </div>
  );
};

export default ProcesoProveedorPage;